import * as mobx from 'mobx';
import { AttributeValue } from '../../../../../../api/attributes.dao';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { TObject } from '../../../../../../infrastructure/type-extensions';
import { FormFieldProps, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { EventAttributeAddValueFieldKey, configList } from './event-attribute-add-value-form.config';
import { IntlShape } from 'react-intl';
import { EXTERNAL_ID_UNIQUE_TEXT, LABEL_UNIQUE_TEXT } from '../event-attribute-form.text';

export class EventAttributeAddValueVM {
    private static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'EventAttributeAddValueVM', configList);
            }
        }();
    }
    @mobx.observable
    public isOpen = false;
    @mobx.observable
    public isSaving = false;
    @mobx.observable
    public labelError: string = '';
    @mobx.observable
    public externalIdError: string = '';
    @mobx.observable
    public attributeValues: AttributeValue[] = [];
    @mobx.observable
    public editingValue: AttributeValue | null = null;
    @mobx.observable
    private originalValueId: number | undefined = undefined;


    private formDataCtrl: AbstractFormViewModelController;
    private readonly onSaveCallback: (value: AttributeValue, originalValueId?: number | undefined) => void;
    private readonly onCloseCallback?: () => void;

    constructor(
        onSaveCallback: (value: AttributeValue) => void,
        initialData: InitialData = {},
        attributeData: AttributeValue[],
        onCloseCallback?: () => void,
    ) {
        this.onSaveCallback = onSaveCallback;
        this.onCloseCallback = onCloseCallback;
        this.formDataCtrl = EventAttributeAddValueVM.createFormVM(initialData);
        this.attributeValues = attributeData;
    }

    public getFormFields = (keys: TObject<string>): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(keys)) {
            formFields[key] = this.formDataCtrl.getFormVmValuePropsByKey(key);
        }
        return formFields;
    };

    @mobx.action
    public open = (value?: AttributeValue): void => {
        if (value) {
            this.editingValue = value;
            this.originalValueId = value.valueId;
            this.formDataCtrl.setValue(EventAttributeAddValueFieldKey.LABEL, value.label);
            this.formDataCtrl.setValue(EventAttributeAddValueFieldKey.STATUS, value.active);
            this.formDataCtrl.setValue(EventAttributeAddValueFieldKey.EXTERNAL_ID, value.externalId || '');
        } else {
            this.editingValue = null;
            this.originalValueId = undefined;
            this.formDataCtrl.resetVm();
        }
        this.labelError = '';
        this.externalIdError = '';
        this.isOpen = true;
    };

    @mobx.action
    public close = (): void => {
        this.isOpen = false;
        this.editingValue = null;
        this.originalValueId = undefined;
        this.formDataCtrl.resetVm();
        this.labelError = '';
        this.externalIdError = '';
        if (this.onCloseCallback) {
            this.onCloseCallback();
        }
    };

    @mobx.action
    public async saveValue(existingValues: AttributeValue[], intl: IntlShape): Promise<void> {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
            return;
        }

        if (!this.validateUniqueness(existingValues, intl)) {
            return;
        }

        this.isSaving = true;
        try {
            const formValues = this.getFormValues();
            if (this.editingValue) {
                const updatedValue: AttributeValue = {
                    ...this.editingValue,
                    ...formValues,
                };
                this.onSaveCallback(updatedValue, this.originalValueId);
            } else {
                const index = existingValues?.length ?? 0;
                const newValue: AttributeValue = {
                    ...formValues,
                    attributeOrder: index,
                };
                this.onSaveCallback(newValue);
            }
            this.close();
        } finally {
            this.isSaving = false;
        }
    }

    @mobx.action
    public toggleStatus = (): void => {
        const current = this.formDataCtrl.getValue(EventAttributeAddValueFieldKey.STATUS) as boolean;
        this.formDataCtrl.setValue(EventAttributeAddValueFieldKey.STATUS, !current);
    };

    @mobx.action
    public clearLabelError = (): void => {
        this.labelError = '';
    };

    @mobx.action
    public clearExternalIdError = (): void => {
        this.externalIdError = '';
    };

    private getFormValues(): { label: string; active: boolean; externalId: string } {
        return {
            label: (this.formDataCtrl.getValue(EventAttributeAddValueFieldKey.LABEL) as string).trim(),
            active: (this.formDataCtrl.getValue(EventAttributeAddValueFieldKey.STATUS) as boolean),
            externalId: (this.formDataCtrl.getValue(EventAttributeAddValueFieldKey.EXTERNAL_ID) as string).trim(),
        };
    }

    private shouldExcludeValue(value: AttributeValue): boolean {
        if (this.originalValueId !== undefined) {
            return value.valueId === this.originalValueId;
        }

        if (!this.editingValue) {
            return false;
        }

        return value.valueId === undefined &&
               value.label === this.editingValue.label &&
               value.attributeOrder === this.editingValue.attributeOrder &&
               value.active === this.editingValue.active &&
               value.externalId === this.editingValue.externalId;
    }

    private validateUniqueness(existing: AttributeValue[], intl: IntlShape): boolean {
        const { label, externalId } = this.getFormValues();

        this.labelError = '';
        this.externalIdError = '';

        const otherValues = existing.filter(v => !this.shouldExcludeValue(v));

        const labelExists = otherValues.some(v => v.label.toLowerCase() === label.toLowerCase());
        const externalIdExists = externalId
            ? otherValues.some(v => v.externalId && v.externalId.toLowerCase() === externalId.toLowerCase())
            : false;

        if (labelExists) {
            this.labelError = intl.formatMessage(LABEL_UNIQUE_TEXT);
        }

        if (externalIdExists) {
            this.externalIdError = intl.formatMessage(EXTERNAL_ID_UNIQUE_TEXT);
        }

        return !(labelExists || externalIdExists);
    }
}